import { Field, FieldProps, Formik } from 'formik';
import React, { VFC } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '@fhs-legacy/universal-components';
import LoadingAnimation from 'components/loading-animation';
import { FormikOTPInput } from 'components/otp-input';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { useAuthContext } from 'state/auth';

const OTP_FIELD_NAME = 'otp-code';

interface IOtpInputField {
  errorMessage?: string | React.ReactNode;
  initialCode?: string | null;
  loading?: boolean;
  otpCodeLength?: number;
  onSubmit(otpValues: string): Promise<void>;
}

export const LoadingContainer = Box.withConfig({
  height: '13.5',
  marginY: '0',
  marginX: '0',
  alignItems: 'center',
  justifyContent: 'center',
});

export const FieldWrapper = Box.withConfig({
  marginY: '0',
  marginX: '0',
  paddingY: '0',
  paddingX: '1',
  width: 'full',
});

const OTP_CODE_LENGTH = 6; // the number of digits in a generated code

export const OtpInputField: VFC<IOtpInputField> = ({
  errorMessage,
  initialCode,
  loading,
  otpCodeLength = OTP_CODE_LENGTH,
  onSubmit,
}) => {
  const { formatMessage } = useIntl();
  const initialOtpFormValues = { [OTP_FIELD_NAME]: initialCode || '' };
  const { loading: authLoading, otpAuthError } = useAuthContext();

  const otpErrorMessage = errorMessage || otpAuthError;

  const handleOtpCodeSubmit = async (
    formValues: { [OTP_FIELD_NAME]: string },
    { setStatus }: FieldProps['form']
  ) => {
    try {
      await onSubmit(formValues[OTP_FIELD_NAME]);
    } catch {
      setStatus({ success: false });
    }
  };

  return (
    <Formik
      initialValues={initialOtpFormValues}
      // @ts-expect-error TS(2322) FIXME: Type '(formValues: {    [OTP_FIELD_NAME]: string;}... Remove this comment to see the full error message
      onSubmit={handleOtpCodeSubmit}
      validateOnChange={false}
    >
      {({ isSubmitting, setFieldValue, touched, values }) => (
        <>
          {authLoading || loading || isSubmitting ? (
            <LoadingContainer
              accessibilityRole="alert"
              accessibilityLabel={formatMessage({ id: 'visuallyHiddenVerifyingOTP' })}
            >
              <LoadingAnimation />
            </LoadingContainer>
          ) : (
            <>
              <VisuallyHidden
                accessibilityLabel={formatMessage({ id: 'youHaveSuccessfullySubmittedTheForm' })}
                aria-live="polite"
              />
              <FieldWrapper>
                <Field
                  name={OTP_FIELD_NAME}
                  forceErrorState={
                    touched[OTP_FIELD_NAME] &&
                    !!otpErrorMessage &&
                    values[OTP_FIELD_NAME].length === otpCodeLength
                  }
                  errorMessage={otpErrorMessage}
                  setFieldValue={setFieldValue}
                  component={FormikOTPInput}
                  disabled={isSubmitting}
                  otpCodeLength={otpCodeLength}
                  submitOnFill
                />
              </FieldWrapper>
            </>
          )}
        </>
      )}
    </Formik>
  );
};
